<script lang="ts">
	import CoinHoldersTableRow from './CoinHoldersTableRow/CoinHoldersTableRow.svelte';
	import type { ITableData } from './types';
	import { sortNumber } from 'utils/sortTableData';
	import SortingArrow from 'components/SortingArrow/SortingArrow.svelte';
	import EmptyTable from 'components/EmptyTable/EmptyTable.svelte';
	import { onMount } from 'svelte';
	import isInViewport from 'utils/isInViewport';
	import { throttle } from 'utils/decorators';
	import { _ } from 'utils/i18/i18n';
	import { getHoldersTableData } from 'services/coin-data/holders-table-data';
	import { browser } from '$app/environment';

	export let dark: boolean;
	export let route: string;
	export let symbol: string;

	let tableData: ITableData[] = [];
	let pressedIconId: string | number;
	let isPressed = true;
	let isInUserViewport = false;
	let isLoaded = false;
	let coinHolderTableRef;

	function getTableData() {
		if (!browser) return;
		getHoldersTableData(route).then((r) => {
			tableData = r;
			isLoaded = true;
		});
	}

	onMount(() => {
		isInUserViewport = isInViewport(coinHolderTableRef);
		if (isInUserViewport) {
			isLoaded = true;
			getTableData();
		} else {
			document.addEventListener('scroll', handleScroll);
		}
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	});

	const handleScroll = throttle(() => {
		isInUserViewport = isInViewport(coinHolderTableRef, 600);
	}, 300);

	$: if (isInUserViewport && !isLoaded) {
		isLoaded = true;
		getTableData();
		document.removeEventListener('scroll', handleScroll);
	}

	function handleClick(e: Event & { currentTarget: HTMLElement }) {
		pressedIconId = e.currentTarget.id;
	}
</script>

<section class="wrapper border-shadow" class:dark bind:this={coinHolderTableRef}>
	{#if tableData.length > 0}
		<table class="table">
			<thead class="table-head no-select">
				<tr class="table-row">
					<th
						id="filterRank"
						class="head-cell rank clickable"
						on:click={(e) => {
							if (pressedIconId !== 'filterRank') isPressed = true;
							handleClick(e);
							tableData = sortNumber(tableData, 'rank', isPressed ? 'ascend' : 'descend');
							isPressed = !isPressed;
						}}
						>#
						<SortingArrow {isPressed} {pressedIconId} idToCompare={'filterRank'} width={11} height={7} />
					</th>
					<th class="head-cell address">{$_('coinDetailed.holdersTable.address')}</th>
					<th class="head-cell wallet">{$_('coinDetailed.holdersTable.wallet')}</th>
					<th
						id="filterBalanceBTC"
						class="head-cell balance-btc clickable"
						on:click={(e) => {
							if (pressedIconId !== 'filterBalanceBTC') isPressed = true;
							handleClick(e);
							tableData = sortNumber(tableData, 'balanceBtc', isPressed ? 'ascend' : 'descend');
							isPressed = !isPressed;
						}}
						>{$_('coinDetailed.holdersTable.balance')}
						{symbol}
						<SortingArrow
							{isPressed}
							{pressedIconId}
							idToCompare={'filterBalanceBTC'}
							width={11}
							height={7}
						/>
					</th>
					<th
						id="filterBalanceUSD"
						class="head-cell balance-usd clickable"
						on:click={(e) => {
							if (pressedIconId !== 'filterBalanceUSD') isPressed = true;
							handleClick(e);
							tableData = sortNumber(tableData, 'balanceUsd', isPressed ? 'ascend' : 'descend');
							isPressed = !isPressed;
						}}
						>{$_('coinDetailed.holdersTable.balanceUSD')}
						<SortingArrow
							{isPressed}
							{pressedIconId}
							idToCompare={'filterBalanceUSD'}
							width={11}
							height={7}
						/>
					</th>

					<th
						id="filterCoinsAmount"
						class="head-cell coins-percentage clickable"
						on:click={(e) => {
							if (pressedIconId !== 'filterCoinsAmount') isPressed = true;
							handleClick(e);
							tableData = sortNumber(tableData, 'coinAmount', isPressed ? 'ascend' : 'descend');
							isPressed = !isPressed;
						}}
						>% {$_('coinDetailed.holdersTable.ofCoins')}
						<SortingArrow
							{isPressed}
							{pressedIconId}
							idToCompare={'filterCoinsAmount'}
							width={11}
							height={7}
						/>
					</th>
					<th
						id="posses-change"
						class="head-cell posses-change clickable"
						on:click={(e) => {
							if (pressedIconId !== 'posses-change') isPressed = true;
							handleClick(e);
							tableData = sortNumber(tableData, 'weekChange', isPressed ? 'ascend' : 'descend');
							isPressed = !isPressed;
						}}
						>7d {$_('coinDetailed.holdersTable.change')}
						<SortingArrow
							{isPressed}
							{pressedIconId}
							idToCompare={'posses-change'}
							width={11}
							height={7}
						/>
					</th>
				</tr>
			</thead>
			{#key tableData}
				<tbody class="table-body">
					{#if typeof tableData === 'undefined' || tableData?.length === 0}
						<EmptyTable {dark} colAmountFromTabletView={7} colAmountMobView={7} tableType="holders" />
					{:else}
						{#each tableData as item}
							<CoinHoldersTableRow data={item} {dark} />
						{/each}
					{/if}
				</tbody>
			{/key}
		</table>
	{/if}
	</section>

<style lang="scss">
	@import 'сoinHoldersTable';
</style>
